import { signOut, useSessionQuery } from '@frontends/data-access-spg-auth';
import { DsIconButton, DsSidebar, type SidebarLogo, useScreenSize } from '@frontends/ds';
import { useTheme } from '@frontends/util-theme-provider';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { RouterProvider as RacRouterProvider } from 'react-aria-components';
import { useTranslation } from 'react-i18next';
import { Outlet, useHref, useLocation, useNavigate } from 'react-router';
import logoDark from '../../assets/brand/logo-dark.svg';
import logoLight from '../../assets/brand/logo-light.svg';
import logoShortDark from '../../assets/brand/logo-short-dark.svg';
import logoShortLight from '../../assets/brand/logo-short-light.svg';
import { getSidebarSections, PATHS } from './navigation';

export function AppLayout(): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { isDesktop } = useScreenSize();
  const [isMenuOpen, setMenuOpen] = useState(false);
  const queryClient = useQueryClient();
  const { data: operator } = useSessionQuery();
  const { resolvedMode } = useTheme();

  const logo: SidebarLogo = {
    alt: t('nav.logo'),
    fullLogoSrc: resolvedMode === 'dark' ? logoDark : logoLight,
    shortLogoSrc: resolvedMode === 'dark' ? logoShortDark : logoShortLight,
    href: '/',
  };

  const handleSignOut = async (): Promise<void> => {
    await signOut();
    queryClient.clear();
    void navigate(PATHS.login, { replace: true });
  };

  return (
    <RacRouterProvider navigate={(path) => void navigate(path)} useHref={useHref}>
      <div className="flex min-h-dvh">
        <DsSidebar
          aria-label={t('nav.label')}
          logo={logo}
          sections={getSidebarSections(t)}
          currentPath={pathname}
          isMobileOpen={isMenuOpen}
          onMobileOpenChange={setMenuOpen}
          profile={operator && { name: operator.displayName, caption: operator.email }}
          bottomItems={[
            { id: 'sign-out', icon: 'log-out', label: t('nav.signOut'), onAction: () => void handleSignOut() },
          ]}
        />

        <div className="flex min-w-0 flex-1 flex-col">
          {!isDesktop && (
            <header className="flex items-center gap-2 p-4">
              <DsIconButton name="panel-left" aria-label={t('nav.open')} onPress={() => setMenuOpen(true)} />
            </header>
          )}

          {/* On desktop the page header row's centre meets the sidebar logo's: the logo's centre sits 32px
              down, minus half the 40px header row = 12px */}
          <main className="flex-1 p-6 lg:pt-3">
            <Outlet />
          </main>
        </div>
      </div>
    </RacRouterProvider>
  );
}
